<template>
    <div class="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
        <TopNav @toggle-sidebar="toggleSidebar" />
        <SideMenu :isOpen="isSidebarOpen" @close-sidebar="isSidebarOpen = false" />

        <!-- Confirm Delete Modal -->
        <div v-if="showConfirmDeleteModal"
            class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-sm w-full">
                <h2 class="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Confirm Delete</h2>
                <p class="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete this {{ deleteTarget?.type }}? This action cannot be undone.
                </p>
                <div class="flex justify-end space-x-4">
                    <button @click="showConfirmDeleteModal = false"
                        class="px-4 py-2 bg-gray-500 text-white rounded-lg">Cancel</button>
                    <button @click="deleteInstanceItem"
                        class="px-4 py-2 bg-red-600 text-white rounded-lg">Delete</button>
                </div>
            </div>
        </div>

        <!-- Instance Info -->
        <main class="pt-20 h-auto bg-gray-200 shadow-md flex justify-center">
            <div class="max-w-screen-lg w-full md:ml-64">
                <div class="mb-4 flex justify-between items-center">
                    <router-link :to="$routeWithBranch('LmsInstances')"
                        class="mt-4 ml-4 flex items-center text-primary-700 text-md font-bold focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span class="ml-2">Back</span>
                    </router-link>
                </div>

                <header class="text-gray-800 py-6 px-10 flex justify-between items-center rounded-lg">
                    <div>
                        <h2 class="text-md font-semibold mb-3">LMS Instance</h2>
                        <h1 class="text-4xl font-semibold">{{ instance.name }}</h1>
                        <p class="mt-2 text-gray-600">{{ instance.domain }}</p>

                        <div class="mt-4 flex flex-wrap gap-4">
                            <div class="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-md">
                                <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400">Licenses Used</h3>
                                <p class="text-lg font-bold">{{ licenseUsage }}</p>
                            </div>
                            <div class="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-md">
                                <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400">Branches Used</h3>
                                <p class="text-lg font-bold">{{ branchUsage }}</p>
                            </div>
                            <div class="bg-white dark:bg-gray-700 p-4 rounded-lg shadow-md">
                                <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400">Subscription Status
                                </h3>
                                <p class="text-lg font-bold">{{ actualSubscriptionStatus }}</p>

                            </div>
                        </div>
                    </div>

                    <div class="flex space-x-4">
                        <router-link :to="$routeWithBranch('EditInstance', { params: { id: instance.id } })"
                            class="bg-gray-700 text-white font-semibold hover:bg-gray-800 py-2 px-4 rounded inline-flex items-center">
                            Edit Instance
                        </router-link>
                        <button @click="showConfirmDeleteModal = true"
                            class="bg-red-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700">
                            Delete Instance
                        </button>
                    </div>
                </header>

                <div class="border-b border-gray-200 dark:border-gray-700 mt-4">
                    <nav class="flex space-x-4 -mb-px">
                        <button @click="activeTab = 'users'" :class="tabClass('users')"
                            class="py-4 px-6 border-b-2 font-medium text-sm focus:outline-none">
                            Users
                        </button>
                        <button @click="activeTab = 'courses'" :class="tabClass('courses')"
                            class="py-4 px-6 border-b-2 font-medium text-sm focus:outline-none">
                            Courses
                        </button>
                        <button @click="activeTab = 'branches'" :class="tabClass('branches')"
                            class="py-4 px-6 border-b-2 font-medium text-sm focus:outline-none">
                            Branches
                        </button>
                        <button @click="activeTab = 'subscriptions'" :class="tabClass('subscriptions')"
                            class="py-4 px-6 border-b-2 font-medium text-sm focus:outline-none">
                            Subscriptions
                        </button>

                    </nav>
                </div>
            </div>
        </main>

        <!-- Tabs Content -->
        <main class="p-6 md:ml-64 h-auto">
            <section class="max-w-screen-lg mx-auto mt-6">
                <div v-if="loading" class="text-center py-10">
                    <svg aria-hidden="true"
                        class="w-14 h-14 mx-auto text-gray-200 animate-spin dark:text-gray-600 fill-secondary-600"
                        viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor" />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill" />
                    </svg>
                    <span class="sr-only">Loading...</span>
                </div>

                <!-- Users Tab -->
                <div v-if="!loading && activeTab === 'users'"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <div class="flex justify-between mb-4">
                        <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">Users in this Instance</h3>
                        <router-link
                            :to="$routeWithBranch('CreateInstanceUser', { params: { instance_id: instance.id } })"
                            class="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 font-semibold">
                            + Create User
                        </router-link>
                    </div>
                    <div v-if="instance.users && instance.users.length">
                        <div class="overflow-x-auto">
                            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead
                                    class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th class="px-6 py-3">Name</th>
                                        <th class="px-6 py-3">Email</th>
                                        <th class="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="user in instance.users" :key="user.id"
                                        class="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <td class="px-6 py-4">{{ user.first_name }} {{ user.last_name }}</td>
                                        <td class="px-6 py-4">{{ user.email }}</td>
                                        <td class="px-6 py-4 space-x-2">
                                            <router-link :to="$routeWithBranch('ViewUser', { params: { id: user.id } })"
                                                class="text-primary-600 hover:underline">View</router-link>
                                            <router-link :to="{
                                                name: 'EditInstanceUser',
                                                params: { instance_id: instance.id, user_id: user.id }
                                            }" class="text-blue-600 hover:underline">
                                                Edit
                                            </router-link>
                                            <button @click="confirmDelete('user', user.id)"
                                                class="text-red-600 hover:underline">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else class="text-center text-gray-500 dark:text-gray-400">No users found.</div>
                </div>

                <!-- Courses Tab -->
                <div v-if="!loading && activeTab === 'courses'"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Courses in this Instance
                    </h3>
                    <div v-if="instance.courses && instance.courses.length">
                        <div class="overflow-x-auto">
                            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead
                                    class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th class="px-6 py-3">Course Title</th>
                                        <th class="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="course in instance.courses" :key="course.id"
                                        class="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <td class="px-6 py-4">{{ course.title }}</td>
                                        <td class="px-6 py-4 space-x-2">
                                            <router-link
                                                :to="$routeWithBranch('ViewCourse', { params: { id: course.id } })"
                                                class="text-primary-600 hover:underline">View</router-link>
                                            <router-link
                                                :to="$routeWithBranch('EditCourse', { params: { id: course.id } })"
                                                class="text-blue-600 hover:underline">Edit</router-link>
                                            <button @click="confirmDelete('course', course.id)"
                                                class="text-red-600 hover:underline">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else class="text-center text-gray-500 dark:text-gray-400">No courses found.</div>
                </div>

                <!-- Branches Tab -->
                <div v-if="!loading && activeTab === 'branches'"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Branches for this Instance
                    </h3>
                    <div v-if="instance.branches && instance.branches.length">
                        <div class="overflow-x-auto">
                            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead
                                    class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th class="px-6 py-3">Branch Name</th>
                                        <th class="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="branch in instance.branches" :key="branch.id"
                                        class="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <td class="px-6 py-4">{{ branch.name }}</td>
                                        <td class="px-6 py-4 space-x-2">
                                            <router-link
                                                :to="$routeWithBranch('ViewBranch', { params: { id: branch.id } })"
                                                class="text-primary-600 hover:underline">View</router-link>
                                            <router-link
                                                :to="$routeWithBranch('EditBranch', { params: { id: branch.id } })"
                                                class="text-blue-600 hover:underline">Edit</router-link>
                                            <button @click="confirmDelete('branch', branch.id)"
                                                class="text-red-600 hover:underline">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else class="text-center text-gray-500 dark:text-gray-400">No branches found.</div>
                </div>
                <div v-if="!loading && activeTab === 'subscriptions'"
                    class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                    <div class="flex justify-between mb-4">
                        <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">Subscriptions</h3>
                        <router-link
                            :to="$routeWithBranch('CreateSubscription', { params: { instance_id: instance.id } })"
                            class="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 font-semibold">
                            + Create Subscription
                        </router-link>
                    </div>
                    <div v-if="instance.subscriptions && instance.subscriptions.length">
                        <div class="overflow-x-auto">
                            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                <thead
                                    class="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                                    <tr>
                                        <th class="px-6 py-3">Status</th>
                                        <th class="px-6 py-3">Billing</th>
                                        <th class="px-6 py-3">Amount</th>
                                        <th class="px-6 py-3">Trial Ends</th>
                                        <th class="px-6 py-3">Plan Ends</th>
                                        <th class="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="sub in instance.subscriptions" :key="sub.id"
                                        class="bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <td class="px-6 py-4 capitalize">{{ sub.status }}</td>
                                        <td class="px-6 py-4">{{ sub.billing_interval }}</td>
                                        <td class="px-6 py-4">£{{ sub.amount?.toFixed(2) }}</td>
                                        <td class="px-6 py-4">{{ formatDate(sub.trial_ends_at) }}</td>
                                        <td class="px-6 py-4">{{ formatDate(sub.plan_ends_at) }}</td>
                                        <td class="px-6 py-4 space-x-2">
                                            <router-link
                                                :to="$routeWithBranch('EditSubscription', { params: { instance_id: instance.id, id: sub.id } })">
                                                Edit
                                            </router-link>

                                            <button @click="confirmDelete('subscription', sub.id)"
                                                class="text-red-600 hover:underline">Delete</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div v-else class="text-center text-gray-500 dark:text-gray-400">No subscriptions found.</div>
                </div>

            </section>

        </main>
    </div>
</template>

<script>
import TopNav from "@/components/headers/TopNav.vue";
import SideMenu from "@/components/SideMenu.vue";
import axios from "axios";

export default {
    components: { TopNav, SideMenu },
    data() {
        return {
            instance: {},
            loading: true,
            activeTab: "users",
            showConfirmDeleteModal: false,
            deleteTarget: null,
            isSidebarOpen: false,
            search: {
                users: '',
                branches: '',
                courses: '',
                subscriptions: '',
            },
            pagination: {
                users: { page: 1, perPage: 10 },
                branches: { page: 1, perPage: 10 },
                courses: { page: 1, perPage: 10 },
                subscriptions: { page: 1, perPage: 10 },
            },
        };
    },
    computed: {
        actualSubscriptionStatus() {
            const sub = this.instance.subscription;
            if (!sub) return 'None';

            if (sub.status === 'active') {
                return this.isTodayOrFuture(sub.plan_ends_at) ? 'Active' : 'Expired';
            }

            if (sub.status === 'trialing') {
                return this.isTodayOrFuture(sub.trial_ends_at) ? 'Trialing' : 'Expired';
            }

            return sub.status.charAt(0).toUpperCase() + sub.status.slice(1);
        },
        filteredUsers() {
            const term = this.search.users.toLowerCase();
            return this.instance.users?.filter(user =>
                `${user.first_name} ${user.last_name}`.toLowerCase().includes(term) ||
                user.email.toLowerCase().includes(term)
            ) || [];
        },
        paginatedUsers() {
            const { page, perPage } = this.pagination.users;
            const start = (page - 1) * perPage;
            return this.filteredUsers.slice(start, start + perPage);
        },
        totalUserPages() {
            return Math.ceil(this.filteredUsers.length / this.pagination.users.perPage);
        },
        licenseUsage() {
            return this.instance.license_count != null && this.instance.used_licenses != null
                ? `${this.instance.used_licenses} / ${this.instance.license_count}`
                : 'N/A';
        },
        branchUsage() {
            return this.instance.branch_limit
                ? `${this.instance.branches?.length ?? 0} / ${this.instance.branch_limit}`
                : 'Unlimited';
        }

    },
    methods: {
        isTodayOrFuture(dateStr) {
            if (!dateStr) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Strip time

            const compare = new Date(dateStr);
            compare.setHours(0, 0, 0, 0); // Strip time

            return compare >= today;
        },
        toggleSidebar() {
            this.isSidebarOpen = !this.isSidebarOpen;
        },
        async fetchInstance() {
            this.loading = true;
            try {
                const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                    query: `
              query($id: Int!) {
                lmsInstance(id: $id) {
                  id
                  name
                  domain
                  license_count
                  branch_limit
                  remaining_licenses
                  used_licenses
                subscription {
                id
                status
                trial_ends_at
                plan_ends_at
                amount
                billing_interval
                }
                users { id first_name last_name email }
                  courses { id title }
                  branches { id name }
                  subscriptions {
                    id
                    status
                    billing_interval
                    amount
                    trial_ends_at
                    plan_ends_at
                }
                }
              }
            `,
                    variables: { id: parseInt(this.$route.params.id) },
                });
                this.instance = response.data.data.lmsInstance;
                console.log("Instance fetched:", this.instance);
            } catch (error) {
                console.error("Error fetching instance:", error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(date) {
            if (!date) return 'N/A';
            const d = new Date(date);
            return d.toLocaleDateString('en-GB'); // Formats as dd/mm/yyyy
        },
        confirmDelete(type, id) {
            this.deleteTarget = { type, id };
            this.showConfirmDeleteModal = true;
        },
        async deleteInstanceItem() {
            if (!this.deleteTarget) return;
            const { type, id } = this.deleteTarget;

            try {
                if (type === 'subscription') {
                    await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                        query: `mutation($id: Int!) { deleteSubscription(id: $id) }`,
                        variables: { id: parseInt(id) },
                    });
                }

                if (type === 'user') {
                    await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                        query: `mutation($id: ID!) {
          deleteUser(id: $id) {
            id
          }
        }`,
                        variables: { id: parseInt(id) },
                    });
                }

                if (type === 'branch') {
                    await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                        query: `mutation($branch_id: Int!) {
          DeleteBranch(branch_id: $branch_id) {
            id
          }
        }`,
                        variables: { branch_id: parseInt(id) },
                    });
                }

                if (type === 'course') {
                    await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
                        query: `mutation($id: Int!) {
          deleteCourse(id: $id)
        }`,
                        variables: { id: parseInt(id) },
                    });
                }

                await this.fetchInstance();
            } catch (error) {
                console.error(`Error deleting ${type}:`, error);
            } finally {
                this.showConfirmDeleteModal = false;
                this.deleteTarget = null;
            }
        },
        tabClass(tab) {
            return {
                "border-blue-600 text-blue-600 dark:text-blue-300": this.activeTab === tab,
                "border-transparent text-gray-500": this.activeTab !== tab,
            };
        },
    },
    created() {
        this.fetchInstance();
    },
};
</script>